import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, CircularProgress,
  Divider, FormControlLabel, IconButton, Paper, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { CODIGO, SUPERFICIE, fmtData, getItem } from './estilo';

const ROTA = '/admin/snapshot';

const INTERVALO_MS = 5000;

/** Cartão de contagem da fila. */
function Cartao({ icone, rotulo, valor, cor, ajuda }) {
  return (
    <Tooltip title={ajuda}>
      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180, ...SUPERFICIE }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ color: cor, display: 'flex' }}>{icone}</Box>
          <Box>
            <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: cor }}>
              {valor}
            </Typography>
            <Typography variant="caption">{rotulo}</Typography>
          </Box>
        </Stack>
      </Paper>
    </Tooltip>
  );
}

/**
 * Motivo da falha em portugues.
 *
 * O texto que vem do worker e a mensagem do Go (`dial tcp ...: connect:
 * connection refused`). Quem opera a rede sabe ler isso, mas o que importa aqui
 * e a acao: porta fechada, credencial recusada e equipamento mudo pedem coisas
 * diferentes. O texto original continua visivel logo abaixo — a traducao ajuda,
 * mas nao pode ESCONDER o que o equipamento respondeu.
 */
function explicar(motivo) {
  const m = (motivo || '').toLowerCase();
  if (m.includes('connection refused')) return 'Nada atendeu nessa porta — serviço de SSH parado ou porta errada.';
  if (m.includes('unable to authenticate') || m.includes('handshake failed')) return 'Usuário ou senha recusados pelo equipamento.';
  if (m.includes('i/o timeout') || m.includes('deadline exceeded') || m.includes('timeout')) return 'O equipamento não respondeu dentro do tempo limite.';
  if (m.includes('no route to host') || m.includes('unreachable')) return 'Sem rota até o IP — problema de rede, não do equipamento.';
  if (m.includes('name or service not known') || m.includes('no such host')) return 'O nome não resolveu em DNS.';
  if (!motivo) return 'O worker não registrou o motivo.';
  return null;
}

/**
 * Fila de coleta e dead-letter queue.
 *
 * A DLQ é o motivo desta tela existir. Item ali significa coleta abandonada
 * depois de esgotar as tentativas — e sem uma tela ninguém descobre, porque o
 * run simplesmente fica incompleto sem nada apitar.
 */
export default function FilaSnapshot() {
  const notify = useNotify();

  // A fila e do WORKER, nao da empresa: um so processo atende todas. Ter
  // seletor de empresa aqui contradizia o proprio aviso da tela.
  const empresa = getItem(localStorage, 'hermes')?.empresa || null;
  const [status, setStatus] = useState(null);
  const [dlq, setDlq] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [auto, setAuto] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    document.title = 'IPV7 Hermes - Fila de Coleta';
  }, []);

  const carregar = useCallback(async () => {
    if (!empresa) return;
    setCarregando(true);
    try {
      const [s, d] = await Promise.all([
        api.get(`${ROTA}/run/queue/status`, { params: { empresa } }),
        api.get(`${ROTA}/run/queue/dlq`, { params: { empresa, limit: 100 } }),
      ]);
      setStatus(s.data?.data || null);
      setDlq(d.data?.data || []);
      setErro(null);
    } catch {
      // Falhar em silêncio aqui seria pior que o normal: a tela mostraria os
      // números velhos e passaria a impressão de que a fila está saudável.
      setErro('Não foi possível ler a fila. Os números abaixo podem estar desatualizados.');
    } finally {
      setCarregando(false);
    }
  }, [empresa]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Polling: não há SSE para o estado global da fila, só para o progresso de um
  // run específico.
  useEffect(() => {
    if (!auto || !empresa) return undefined;
    const t = setInterval(carregar, INTERVALO_MS);
    return () => clearInterval(t);
  }, [auto, empresa, carregar]);

  const temDlq = dlq.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#0f172a' }}>Fila de Coleta</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          sx={{ color: '#0f172a' }}
          control={<Switch size="small" checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={<Typography variant="caption">atualizar sozinho</Typography>}
        />
        <Tooltip title="Recarregar">
          <span>
            <IconButton onClick={carregar} disabled={carregando} sx={{ color: '#475569' }}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {(
        <>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <Alert severity="info" sx={{ mb: 2 }}>
            A fila é <strong>do worker</strong>: um só processo atende todas as
            empresas, e estes números somam as coletas de todas elas.
          </Alert>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Cartao
              icone={<HourglassEmptyIcon />} cor="#64748b"
              rotulo="na fila" valor={status?.queued ?? '—'}
              ajuda="Coletas enfileiradas, esperando um worker livre."
            />
            <Cartao
              icone={<PlayCircleOutlineIcon />} cor="#2563eb"
              rotulo="executando" valor={status?.processing ?? '—'}
              ajuda="Coletas que um worker pegou e está executando agora."
            />
            <Cartao
              icone={<ReportProblemIcon />} cor={temDlq ? '#c0392b' : '#16a085'}
              rotulo="abandonadas" valor={status?.dlq ?? '—'}
              ajuda="Dead-letter queue: coletas que falharam em todas as tentativas."
            />
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, ...SUPERFICIE }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 600 }}>Dead-letter queue</Typography>
              {carregando && <CircularProgress size={14} />}
            </Stack>
            <Divider sx={{ my: 1.5 }} />

            {!temDlq && (
              <Alert severity="success">
                Nenhuma coleta abandonada. É o estado esperado.
              </Alert>
            )}

            {temDlq && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {dlq.length} coleta(s) esgotaram as tentativas. Os runs que dependiam
                  delas estão <strong>incompletos</strong> — comparar sem resolver isso
                  produz resultado parcial.
                </Alert>

                {dlq.map((item, i) => {
                  const h = item.host || {};
                  const nome = h.host_name || h.ip_address || `coleta ${i + 1}`;
                  const legivel = explicar(item.reason);

                  return (
                    <Accordion key={i} sx={SUPERFICIE} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack sx={{ width: '100%', pr: 1 }} spacing={0.25}>
                          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{nome}</Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#475569' }}>
                              {h.ip_address}{h.port ? `:${h.port}` : ''}
                            </Typography>
                            {h.vendor && (
                              <Chip size="small" label={h.vendor}
                                sx={{ height: 18, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#475569' }} />
                            )}
                            <Box sx={{ flex: 1 }} />
                            {item.run_id && (
                              <Chip size="small" label={`coleta #${item.run_id}`}
                                sx={{ height: 19, bgcolor: '#e2e8f0', color: '#334155' }} />
                            )}
                            {item.attempt != null && (
                              <Chip size="small" label={`${item.attempt} tentativa(s)`}
                                sx={{ height: 19, bgcolor: '#fee2e2', color: '#991b1b' }} />
                            )}
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {fmtData(item.failed_at)}
                            </Typography>
                          </Stack>

                          {/* O motivo vem junto do titulo: e a unica informacao que
                              decide se vale reabrir o item, e obrigar a expandir tres
                              linhas para descobrir isso e trabalho a toa. */}
                          <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                            {legivel || item.reason}
                          </Typography>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pt: 0 }}>
                        <Table size="small" sx={{ mb: 1.5 }}>
                          <TableBody>
                            {[
                              ['Equipamento', `${nome}${h.ip_address ? ` — ${h.ip_address}${h.port ? `:${h.port}` : ''}` : ''}`],
                              ['Tipo', [h.vendor, h.type, h.protocol].filter(Boolean).join(' · ') || '—'],
                              ['Coleta', item.run_id ? `#${item.run_id}` : '—'],
                              ['Tentativas', item.attempt != null ? String(item.attempt) : '—'],
                              ['Enfileirada em', fmtData(item.enqueued_at)],
                              ['Desistiu em', fmtData(item.failed_at)],
                              ['Tempo limite', item.timeout ? `${item.timeout}s` : '—'],
                            ].map(([k, v]) => (
                              <TableRow key={k}>
                                <TableCell sx={{ width: 160, border: 0, py: 0.4, color: '#64748b' }}>
                                  <Typography variant="caption">{k}</Typography>
                                </TableCell>
                                <TableCell sx={{ border: 0, py: 0.4 }}>
                                  <Typography sx={{ fontSize: 12.5 }}>{v}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Resposta do worker
                        </Typography>
                        <Box sx={{ ...CODIGO, whiteSpace: 'pre-wrap', mt: 0.5, mb: 1.5, color: '#b91c1c' }}>
                          {item.reason || '(sem mensagem)'}
                        </Box>

                        {!!item.commands?.length && (
                          <>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              Comandos que não chegaram a rodar ({item.commands.length})
                            </Typography>
                            <Box sx={{ ...CODIGO, whiteSpace: 'pre-wrap', mt: 0.5 }}>
                              {item.commands.map((c) => c.text || c).join('\n')}
                            </Box>
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}
          </Paper>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, ...SUPERFICIE }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>O que significa cada número</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 150 }}><strong>na fila</strong></TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      Enfileirada, ainda sem worker. Cresce quando há mais coleta que worker.
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>executando</strong></TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      Em execução. Se ficar parada com fila cheia, algum worker travou
                      numa sessão — a retomada devolve o item para a fila.
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>abandonadas</strong></TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      Falhou em todas as tentativas. Não sai daqui sozinha: alguém
                      precisa olhar o erro e disparar de novo.
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
